<template>
  <div class="top-bar">
    <div class="top-bar__left">
      <v-icon size="18" color="blue-lighten-2" class="mr-1">mdi-video</v-icon>
      <span class="top-bar__room-name">{{ roomName }}</span>
    </div>

    <div class="top-bar__center">
      <span class="top-bar__duration">{{ formattedDuration }}</span>
    </div>

    <div class="top-bar__right">
      <!-- Recording indicator — visible to ALL participants when recording -->
      <span v-if="isRecording" class="top-bar__rec">
        <v-icon size="12" color="error" class="rec-pulse mr-1">mdi-circle</v-icon>REC
      </span>

      <!-- Copy meeting link -->
      <v-tooltip v-if="shareLink" :text="copied ? 'Copied!' : 'Copy meeting link'" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            :color="copied ? 'success' : 'blue-lighten-2'"
            class="mr-1"
            @click="copyLink"
          >
            <v-icon size="16">{{ copied ? 'mdi-check' : 'mdi-link-variant' }}</v-icon>
          </v-btn>
        </template>
      </v-tooltip>

      <!-- Waiting room (admin only) -->
      <v-tooltip v-if="isAdmin" text="Waiting room" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            :color="showWaitingRoom ? 'blue-lighten-2' : 'grey-lighten-1'"
            class="mr-1"
            @click="$emit('toggle-waiting-room')"
          >
            <v-icon size="18">mdi-account-clock</v-icon>
            <span v-if="waitingGuestCount > 0" class="top-bar__badge top-bar__badge--amber">{{ waitingGuestCount }}</span>
          </v-btn>
        </template>
      </v-tooltip>

      <!-- Participants -->
      <v-tooltip text="Participants" location="bottom">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            size="x-small"
            variant="text"
            :color="showParticipants ? 'blue-lighten-2' : 'grey-lighten-1'"
            @click="$emit('toggle-participants')"
          >
            <v-icon size="18">mdi-account-group</v-icon>
            <span v-if="participantCount > 0" class="top-bar__badge">{{ participantCount }}</span>
          </v-btn>
        </template>
      </v-tooltip>
    </div>
  </div>
</template>

<script>
export default {
  name: 'TopBar',

  props: {
    roomName: { type: String, default: '' },
    participantCount: { type: Number, default: 1 },
    isRecording: { type: Boolean, default: false },
    startTime: { type: Number, default: null },
    shareLink: { type: String, default: '' },
    isAdmin: { type: Boolean, default: false },
    showParticipants: { type: Boolean, default: false },
    showWaitingRoom: { type: Boolean, default: false },
    waitingGuestCount: { type: Number, default: 0 },
  },

  emits: ['toggle-participants', 'toggle-waiting-room'],

  data() {
    return {
      elapsed: 0,
      timer: null,
      copied: false,
      copyTimer: null,
    };
  },

  computed: {
    formattedDuration() {
      const s = Math.floor(this.elapsed / 1000);
      const h = Math.floor(s / 3600);
      const m = Math.floor((s % 3600) / 60);
      const sec = s % 60;
      const pad = (n) => String(n).padStart(2, '0');
      return h > 0 ? `${pad(h)}:${pad(m)}:${pad(sec)}` : `${pad(m)}:${pad(sec)}`;
    },
  },

  mounted() {
    const base = this.startTime ?? Date.now();
    this.elapsed = Date.now() - base;
    this.timer = setInterval(() => {
      this.elapsed = Date.now() - base;
    }, 1000);
  },

  beforeUnmount() {
    clearInterval(this.timer);
    clearTimeout(this.copyTimer);
  },

  methods: {
    async copyLink() {
      try {
        await navigator.clipboard.writeText(this.shareLink);
      } catch {
        const ta = document.createElement('textarea');
        ta.value = this.shareLink;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copied = true;
      clearTimeout(this.copyTimer);
      this.copyTimer = setTimeout(() => { this.copied = false; }, 2000);
    },
  },
};
</script>

<style scoped>
.top-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 52px;
  padding: 0 16px;
  background: #1e1e1e;
  border-bottom: 1px solid #333;
  flex-shrink: 0;
  color: #e0e0e0;
  font-size: 0.85rem;
}

.top-bar__left {
  display: flex;
  align-items: center;
  min-width: 0;
  flex: 1;
}

.top-bar__room-name {
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 240px;
  color: #fff;
}

.top-bar__center {
  flex: 0 0 auto;
  font-variant-numeric: tabular-nums;
  color: #bdbdbd;
  font-size: 0.9rem;
}

.top-bar__right {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 4px;
}

.top-bar__rec {
  display: flex;
  align-items: center;
  font-size: 0.75rem;
  font-weight: 600;
  color: #ef5350;
  margin-right: 8px;
  letter-spacing: 0.05em;
}

.rec-pulse {
  animation: rec-blink 1.2s ease-in-out infinite;
}

@keyframes rec-blink {
  0%, 100% { opacity: 1; }
  50%       { opacity: 0.2; }
}

.top-bar__badge {
  position: absolute;
  top: 2px;
  right: 2px;
  background: #1976d2;
  color: #fff;
  border-radius: 10px;
  font-size: 0.55rem;
  min-width: 14px;
  height: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 3px;
  pointer-events: none;
}

.top-bar__badge--amber {
  background: #ef6c00;
}

.top-bar__count {
  font-weight: 500;
  color: #bdbdbd;
}
</style>
